// Arena drops (contracts/phase4.md "Admin"): every drop by UTC date with its fill, matches,
// completion, median RTT and flagged answers; each match opens the round-by-round replay.

import { Fragment, useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { useArenaDrops, useConfig } from "../api/queries";
import { DEV_REASON_LABEL, type ArenaDrop, type DropState } from "../api/schemas";
import { tableStyles } from "../components/table";
import { Chip, EmptyState, ErrorState, Field, PageHeader, Segmented, Skeleton, Stat, TextInput } from "../components/ui";
import { addDays, daysBetween, formatLongDate, todayUtc } from "../lib/dates";
import { fmtInt, fmtPct } from "../lib/format";
import a from "./arena.module.css";
import styles from "./pages.module.css";

const STATE_TONE: Record<DropState, string> = {
  scheduled: "muted",
  lobby: "info",
  running: "arena",
  finished: "success",
  rolled_over: "warning",
  aborted: "danger",
};
const STATE_LABEL: Record<DropState, string> = {
  scheduled: "scheduled",
  lobby: "lobby open",
  running: "running",
  finished: "finished",
  rolled_over: "rolled over",
  aborted: "aborted",
};

const isDate = (v: string | null): v is string => Boolean(v && /^\d{4}-\d{2}-\d{2}$/.test(v));
const hhmm = (iso: string) => iso.slice(11, 16);

/** `global-20260926T1800Z` → "18:00"; an ad-hoc `…T120530Z-adhoc` → "12:05:30". */
export function dropTime(dropId: string): string {
  const m = /T(\d{2})(\d{2})(\d{2})?Z/.exec(dropId);
  return m ? `${m[1]}:${m[2]}${m[3] ? `:${m[3]}` : ""}` : dropId;
}

/** "m01" from `global-20260926T1200Z-m01-2f1aed`. */
export const matchLabel = (matchId: string) => /-(m\d{2})-[0-9a-f]+$/.exec(matchId)?.[1] ?? matchId.slice(-6);

export function ArenaPage() {
  const [params, setParams] = useSearchParams();
  const today = todayUtc();
  const to = isDate(params.get("to")) ? params.get("to")! : today;
  const fromRaw = params.get("from");
  const from = isDate(fromRaw) && fromRaw <= to ? fromRaw : addDays(to, -6);
  const span = daysBetween(from, to) + 1;
  const dev = params.get("dev") !== "0";
  const q = useArenaDrops(from, to);
  const config = useConfig();
  const schedule = config.data?.values["arena.schedule"];
  const every = config.data?.values["arena.devEveryMinutes"];

  const set = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null) next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );

  const items = useMemo(() => (q.data?.items ?? []).filter((d) => dev || !d.dev), [q.data, dev]);
  const byDate = useMemo(() => {
    const map = new Map<string, ArenaDrop[]>();
    for (const d of items) map.set(d.date, [...(map.get(d.date) ?? []), d]);
    return [...map.entries()];
  }, [items]);
  const played = items.filter((d) => d.state === "finished");
  const matches = played.reduce((n, d) => n + d.matchCount, 0);
  const entrants = played.reduce((n, d) => n + d.entrants, 0);
  const rtts = played.map((d) => d.medianRttMs).filter((x): x is number => x !== null);
  const completions = played.map((d) => d.completion).filter((x): x is number => x !== null);

  return (
    <>
      <PageHeader
        title="Arena"
        meta={
          <span>
            Drops by UTC date: fill, matches, completion, median RTT and flagged answers.{" "}
            {Array.isArray(schedule) ? (
              <>
                Schedule <span className="mono">{schedule.join(" · ")}</span> UTC
                {typeof every === "number" ? (
                  <>
                    {" "}
                    · dev every <span className="mono">{every}</span> min
                  </>
                ) : null}{" "}
                ·{" "}
                <Link to="/config#arena" className={styles.back}>
                  edit in Config →
                </Link>
              </>
            ) : null}
          </span>
        }
      />
      <div className={styles.filters}>
        <Segmented
          label="Range"
          value={to === today && [1, 7, 14].includes(span) ? String(span) : "custom"}
          onChange={(v) => v !== "custom" && set({ from: addDays(today, -(Number(v) - 1)), to: null })}
          options={[
            { value: "1", label: "Today" },
            { value: "7", label: "7 days" },
            { value: "14", label: "14 days" },
            { value: "custom", label: "Custom" },
          ]}
        />
        <Field label="From">
          <TextInput type="date" value={from} max={to} onChange={(e) => isDate(e.target.value) && set({ from: e.target.value })} aria-label="From (UTC)" />
        </Field>
        <Field label="To">
          <TextInput type="date" value={to} min={from} onChange={(e) => isDate(e.target.value) && set({ to: e.target.value })} aria-label="To (UTC)" />
        </Field>
        <Segmented
          label="Dev drops"
          value={dev ? "show" : "hide"}
          onChange={(v) => set({ dev: v === "show" ? null : "0" })}
          options={[
            { value: "show", label: "With dev drops" },
            { value: "hide", label: "Scheduled only" },
          ]}
        />
      </div>

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className={styles.statStrip} data-testid="arena-summary">
            <Stat label="Drops played" value={q.data ? fmtInt(played.length) : "…"} sub={q.data ? `of ${items.length} in range` : undefined} />
            <Stat label="Rolled over" value={q.data ? fmtInt(items.filter((d) => d.state === "rolled_over").length) : "…"} sub="under 20 at T0" />
            <Stat label="Matches" value={q.data ? fmtInt(matches) : "…"} />
            <Stat label="Players" value={q.data ? fmtInt(entrants) : "…"} sub={played.length ? `${fmtInt(Math.round(entrants / played.length))} per drop` : undefined} />
            <Stat label="Completion" value={completions.length ? fmtPct(completions.reduce((x, y) => x + y, 0) / completions.length) : "—"} sub="matches that finished" />
            <Stat label="Median RTT" value={rtts.length ? `${Math.round(rtts.sort((x, y) => x - y)[rtts.length >> 1]!)} ms` : "—"} />
            <Stat label="Flagged answers" value={q.data ? fmtInt(played.reduce((n, d) => n + d.flaggedAnswers, 0)) : "…"} sub="under 250 ms" />
          </div>

          <div className={tableStyles.wrap} aria-busy={q.isFetching}>
            <table className={tableStyles.table} data-testid="drops">
              <thead>
                <tr>
                  <th>T0 (UTC)</th>
                  <th>Drop</th>
                  <th>State</th>
                  <th data-align="right">Entrants</th>
                  <th>Fill</th>
                  <th>Matches</th>
                  <th data-align="right">Completion</th>
                  <th data-align="right">Median RTT</th>
                  <th data-align="right">Flagged</th>
                </tr>
              </thead>
              <tbody>
                {q.isPending
                  ? Array.from({ length: 8 }, (_, i) => (
                      <tr key={i}>
                        {[40, 170, 70, 40, 90, 120, 40, 50, 30].map((w, j) => (
                          <td key={j}>
                            <Skeleton height={12} width={w} />
                          </td>
                        ))}
                      </tr>
                    ))
                  : byDate.map(([date, drops]) => (
                      <Fragment key={date}>
                        <tr className={a.dateRow} data-date={date}>
                          <td colSpan={9}>
                            <span>{formatLongDate(date)}</span>
                            <span className="monoLabel">
                              {drops.length} drop{drops.length === 1 ? "" : "s"}
                              {date === today ? " · today" : ""}
                            </span>
                          </td>
                        </tr>
                        {drops.map((d) => (
                          <DropRow key={d.id} drop={d} />
                        ))}
                      </Fragment>
                    ))}
              </tbody>
            </table>
            {!q.isPending && items.length === 0 ? <EmptyState title="No drops in this range">Drops run on the schedule in Config (`arena.schedule`).</EmptyState> : null}
          </div>
        </>
      )}
    </>
  );
}

function DropRow({ drop: d }: { drop: ArenaDrop }) {
  const capacity = d.capacity * Math.max(1, d.matchCount);
  return (
    <tr data-drop={d.id} data-state={d.state}>
      <td className="mono">{hhmm(d.t0)}</td>
      <td>
        <span className={a.dropId}>
          <span className={tableStyles.mono}>{d.id}</span>
          {d.dev ? (
            <span data-testid="dev-drop" title={d.devReasons.map((r) => DEV_REASON_LABEL[r] ?? r).join(" · ") || "dev"}>
              <Chip tone="violet">
                {/* backend/api's reasons, the bots with their count (one chip keeps the row short) */}
                {d.devReasons.length ? `dev · ${d.devReasons.map((r) => (r === "bots" && d.bots ? `${d.bots} bots` : r.replace(/_/g, " "))).join(", ")}` : "dev"}
              </Chip>
            </span>
          ) : null}
          {d.bots && !(d.dev && d.devReasons.includes("bots")) ? (
            <Chip tone="muted" title="Practice bots (dev and QA only)">
              {`${d.bots} bot${d.bots === 1 ? "" : "s"}`}
            </Chip>
          ) : null}
        </span>
      </td>
      <td>
        <Chip tone={STATE_TONE[d.state]}>{STATE_LABEL[d.state]}</Chip>
      </td>
      <td data-align="right" className="mono" title={d.reservations ? `${d.reservations} reserved a seat` : undefined}>
        {d.state === "scheduled" && !d.entrants ? <span className={tableStyles.faint}>{d.reservations ? `${d.reservations} res.` : "—"}</span> : fmtInt(d.entrants)}
      </td>
      <td>
        {d.fill === null ? (
          <span className={tableStyles.faint}>—</span>
        ) : (
          <span className={a.fill} title={`${d.entrants} of ${capacity} seats`}>
            <span className={styles.meter} data-tone={d.fill >= 0.9 ? "accent" : undefined}>
              <span style={{ width: `${Math.min(100, d.fill * 100)}%` }} />
            </span>
            <span className="mono">{fmtPct(d.fill)}</span>
          </span>
        )}
      </td>
      <td>
        {d.matches.length ? (
          <span className={a.matchLinks}>
            {d.matches.map((m) => (
              <Link key={m.matchId} to={`/arena/matches/${encodeURIComponent(m.matchId)}`} className={a.matchLink} data-aborted={m.aborted || undefined} title={m.winner ? `Won by ${m.winner.name}` : m.aborted ? "Aborted" : undefined}>
                <span>{matchLabel(m.matchId)}</span>
                {m.dev ? <span className={a.matchDev} title="The results webhook said dev: true">dev</span> : null}
                <span className={a.matchPlayers}>{m.players}</span>
                {m.flaggedAnswers ? <span className={a.matchFlag}>⚑{m.flaggedAnswers}</span> : null}
              </Link>
            ))}
          </span>
        ) : d.state === "rolled_over" ? (
          <span className={tableStyles.dim} title={d.nextDropId ? `Rolled into ${d.nextDropId}` : undefined}>
            → {d.nextDropId ? dropTime(d.nextDropId) : "next drop"}
          </span>
        ) : (
          <span className={tableStyles.faint}>{d.matchCount || "—"}</span>
        )}
      </td>
      <td data-align="right" className="mono">
        {d.completion === null ? <span className={tableStyles.faint}>—</span> : fmtPct(d.completion)}
      </td>
      <td data-align="right" className="mono">
        {d.medianRttMs === null ? <span className={tableStyles.faint}>—</span> : `${Math.round(d.medianRttMs)} ms`}
      </td>
      <td data-align="right">
        {d.flaggedAnswers ? <Chip tone="warning">{String(d.flaggedAnswers)}</Chip> : <span className={tableStyles.faint}>0</span>}
      </td>
    </tr>
  );
}
